import { useEnvironment } from '../context/EnvironmentContext';
import styles from './SandboxBanner.module.css';

/**
 * The single sandbox notice for the whole app. There is deliberately ONE of these:
 * a second banner beside it is how a page ends up saying "Test Mode" and "LIVE" at
 * the same time.
 *
 * Two independent triggers, one surface:
 *  - `isSandbox` from EnvironmentContext — a property of THE ORIGIN the bundle was
 *    served from (`GET /api/environment`), never client state. This is the real one.
 *  - `legacyTestMode` — the superseded per-user `users.sandbox_mode` flag, which still
 *    exists ON LIVE and still needs to warn its users. Task 22 retires it; until then
 *    dropping it would silently remove a live notice, so it is folded in here rather
 *    than left to a rival banner.
 *
 * The environment wins when both are set: an origin-level SANDBOX is the stronger claim.
 *
 * NOT COLOUR ALONE — the literal word SANDBOX is in the DOM, so the notice survives
 * greyscale, colour-blindness and a screenshot with the stylesheet stripped.
 */
const SandboxBanner = ({ legacyTestMode = false }) => {
  const { isSandbox } = useEnvironment();

  if (isSandbox) {
    return (
      <div className={styles.banner} role="status" data-environment-banner="sandbox">
        <span className={styles.dot} aria-hidden="true" />
        <span className={styles.title}>SANDBOX — TEST DATA ONLY</span>
        <span className={styles.text}>
          This is the test environment. Nothing here is a real member, a real order or a real charge.
        </span>
      </div>
    );
  }

  if (legacyTestMode) {
    return (
      <div className={styles.banner} role="status" data-environment-banner="test-mode">
        <span className={styles.dot} aria-hidden="true" />
        <span className={styles.title}>Test Mode</span>
        <span className={styles.text}>
          Anything you create here is sandboxed — it stays out of back-office lists, reports and metrics.
        </span>
      </div>
    );
  }

  return null;
};

export default SandboxBanner;
